<script setup lang="ts">
/**
 * The sill — the same footer on the landing page and on the news pages, for
 * the same reason `SiteHeader` is shared. See that component's own note.
 *
 * Fully self-contained: it fetches the admin-managed company/social info
 * itself rather than taking it as a prop (three different pages would
 * otherwise each have to fetch it and pass it down), and it owns the legal
 * document overlay outright — `legalSlug` and `<LegalOverlay>` both live
 * here, with their own Escape-to-close, so mounting this footer is the
 * whole of what a page has to do to offer the legal documents.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

import BrandLogo from '../ui/BrandLogo.vue';
import LegalOverlay from './LegalOverlay.vue';
import { navigateNews } from '../state/news-route';
import { goToSiteSection } from './site-nav';
import { NAV } from './landing-content';
import {
  fetchLegalList,
  fetchSiteInfo,
  type CompanyInfo,
  type PaymentMethodInfo,
  type SocialLink,
} from './site-api';
import { iconFor } from './social-icons';
import type { LegalDoc } from '../state/legal-slugs';

const legalSlug = ref<string | null>(null);
const legalDocs = ref<LegalDoc[]>([]);
const company = ref<CompanyInfo | null>(null);
const social = ref<SocialLink[]>([]);
const paymentMethods = ref<PaymentMethodInfo[]>([]);
const year = new Date().getFullYear();

onMounted(() => {
  void fetchSiteInfo().then((info) => {
    company.value = info.company;
    social.value = info.social;
    paymentMethods.value = info.paymentMethods;
  });
  void fetchLegalList().then((list) => {
    legalDocs.value = list;
  });
});

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && legalSlug.value) legalSlug.value = null;
}

onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <footer class="sill">
    <div class="wrap">
      <div class="sill-top">
        <div class="sill-brand">
          <BrandLogo class="sill-logo" />
          <address v-if="company && (company.name || company.address)" class="sill-brand-address">
            <span v-if="company.name">{{ company.name }}</span>
            <span v-if="company.address">{{ company.address }}</span>
          </address>
        </div>

        <nav class="sill-menu" aria-label="Footer">
          <div class="sill-menu-col sill-menu-col--game">
            <p class="sill-menu-heading">Game</p>
            <div class="sill-menu-links">
              <a
                v-for="item in NAV"
                :key="item.href"
                :href="item.href"
                @click.prevent="goToSiteSection(item.href)"
                >{{ item.label }}</a
              >
              <a href="/news" @click.prevent="navigateNews('/news')">News</a>
            </div>
          </div>
          <div class="sill-menu-col">
            <p class="sill-menu-heading">Company</p>
            <button
              v-for="doc in legalDocs"
              :key="doc.slug"
              type="button"
              @click="legalSlug = doc.slug"
            >
              {{ doc.title }}
            </button>
            <a v-if="company?.email" :href="`mailto:${company.email}`">Contact</a>
          </div>
        </nav>
      </div>

      <!-- One logo per configured platform, wired to its URL. `iconFor` is a
           frontend-only map from the admin's platform picker's seven values
           to a shipped SVG; a link on an eighth value (created before the
           picker existed, or against the API directly) has no icon and falls
           back to its platform name as plain text rather than a broken
           image. -->
      <div v-if="social.length" class="sill-social">
        <p class="sill-menu-heading">Follow</p>
        <ul class="sill-social-list">
          <li v-for="link in social" :key="link.url">
            <a :href="link.url" target="_blank" rel="noopener noreferrer" :aria-label="link.platform">
              <img
                v-if="iconFor(link.platform)"
                :src="iconFor(link.platform)!"
                :alt="link.platform"
                class="sill-social-icon"
              />
              <span v-else>{{ link.platform }}</span>
            </a>
          </li>
        </ul>
      </div>

      <!-- The payment row. Real methods, admin-managed: `GET /api/site-info`
           only ever sends the ones an operator has switched on in the admin
           panel's Payment methods pane, sorted by that pane's own order — see
           `site-api.ts`. Every one of the 88 catalogue entries starts
           disabled, so a fresh install hides this row entirely rather than
           showing an empty band (`v-if` below). No hover and no cursor
           change, same as before — these are statements of fact, not
           controls. -->
      <div v-if="paymentMethods.length" class="sill-pay">
        <p class="sill-menu-heading">Payment methods</p>
        <ul class="sill-pay-list">
          <li v-for="method in paymentMethods" :key="method.slug" class="sill-pay-item">
            <img :src="`/payment-methods/${method.icon}.svg`" :alt="method.name" class="sill-pay-icon" />
          </li>
        </ul>
      </div>

      <p class="sill-copy">
        © {{ year }} {{ company?.name || 'Steel of War' }}. All rights reserved.
      </p>
      <address v-if="company?.registration" class="sill-entity">
        <span>Company number {{ company.registration }}</span>
      </address>
    </div>
  </footer>

  <LegalOverlay :slug="legalSlug" @close="legalSlug = null" />
</template>
